/**
 * Focus Copy Helpers
 * Centralized copy generation for focus cards
 */

/**
 * Get copy for Active Focus card
 * @param {Object} conversation - Conversation object with title
 * @param {Object} lastMessage - Last user message object with content
 * @returns {{title: string, description: string}}
 */
export function getActiveFocusCopy(conversation, lastMessage) {
  const title = 'Pick up where you left off';
  
  const messageContent = lastMessage?.content || '';
  
  // Create description with last message snippet
  let description;
  if (messageContent) {
    const trimmedMessage = messageContent.length > 160
      ? messageContent.substring(0, 160) + '...'
      : messageContent;
    description = `You were last working on: "${trimmedMessage}". Jump back in and keep it moving.`;
  } else {
    description = 'You were last working on this thread. Jump back in and keep it moving.';
  }
  
  return { title, description };
}

/**
 * Get copy for Suggested Focus card
 * @param {string|null} channel - Channel name (Instagram, TikTok, YouTube, etc.) or null for generic
 * @param {Object} signals - Signals object with activity counts
 * @returns {{title: string, description: string}}
 */
export function getSuggestedFocusCopy(channel, signals) {
  let title;
  let description;
  
  switch (channel) {
    case 'Instagram':
      title = 'Turn your saves into a series.';
      description = 'You have been saving a lot from Instagram. Let Swirl turn them into a clear content arc you can actually ship.';
      break;
    case 'TikTok':
      title = 'Shape a TikTok series from your saves.';
      description = 'Your TikTok saves are stacking up. Swirl can turn them into a simple repeatable series.';
      break;
    case 'YouTube':
      title = 'Turn your deep dives into a plan.';
      description = 'You are collecting long form videos. Swirl can pull the threads and turn them into a focused plan.';
      break;
    case null:
    case undefined:
      // No content case
      title = 'Feed Swirl with your world.';
      description = 'Upload a few posts, screenshots, or videos so Swirl has something real to work with.';
      break;
    default:
      title = 'Make your saved content work harder.';
      description = 'You are saving links and clips across channels. Swirl can group them into one theme you can move on.';
      break;
  }
  
  return { title, description };
}

/**
 * Get copy for Insight card
 * @param {string} insightState - State key: 'Thinker', 'Collector', or 'Quiet'
 * @param {Object} metrics - Metrics object with activity data
 * @returns {{title: string, description: string}}
 */
export function getInsightCopy(insightState, metrics) {
  let title;
  let description;
  
  switch (insightState) {
    case 'Thinker':
      title = 'You are in thinking mode';
      description = 'You are talking to Swirl and adding content. Now choose one thread to turn into a concrete project.';
      break;
    case 'Collector':
      title = 'You are in collection mode';
      description = 'You are saving a lot of content and not talking to Swirl much. Pick one folder or channel and turn it into a focus.';
      break;
    case 'Quiet':
    default:
      title = 'You are in quiet mode';
      description = 'Things have been slow. Upload a few things or start a new question so Swirl has something fresh to work with.';
      break;
  }
  
  return { title, description };
}

